// src/pages/TextesReglementaires/components/LegalPageLayout.jsx

import React from "react";
import {
  FiAlertCircle,
  FiExternalLink,
  FiFileText,
  FiMail,
  FiMapPin,
  FiPhone,
  FiShield,
} from "react-icons/fi";

import PageHero from "../../../components/common/PageHero";
import PageSectionHeader from "../../../components/common/PageSectionHeader";
import Reveal from "../../../components/ui/Reveal";
import ScrollIndicator from "../../Home/ScrollIndicator";

export default function LegalPageLayout({
  title,
  eyebrow = "Textes réglementaires",
  description,
  heroImageUrl = "https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg",
  identity,
  updatedAt,
  children,
}) {
  return (
    <>
      <PageHero
        title={title}
        imageUrl={heroImageUrl}
        crumbs={[
          { label: "Accueil", href: "/" },
          { label: "Textes réglementaires" },
          { label: title },
        ]}
      />

      <ScrollIndicator />

      <main className="mx-auto max-w-[1240px] px-4 pb-16 pt-6 sm:px-6 md:pt-8">
        <PageSectionHeader
          eyebrow={eyebrow}
          title={title}
          description={description}
        >
          <div className="rounded-3xl border border-slate-200 bg-white/80 px-5 py-4 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-950/70">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Mise à jour
            </p>
            <p className="mt-1 text-lg font-bold text-slate-950 dark:text-white">
              {updatedAt}
            </p>
          </div>
        </PageSectionHeader>

        <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
          <Reveal>
            <article className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-7 md:p-8">
              <div className="mb-6 flex items-start gap-3 rounded-3xl border border-sky-100 bg-sky-50 p-4 text-sm leading-7 text-sky-900 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-100">
                <FiAlertCircle className="mt-1 h-5 w-5 shrink-0" />
                <p>
                  Ces informations sont fournies pour informer les usagers du
                  site de la mairie. Elles doivent être adaptées aux pratiques
                  réelles de la commune, aux outils utilisés et aux coordonnées
                  officielles.
                </p>
              </div>

              <div
                className="
                  prose prose-slate max-w-none dark:prose-invert
                  prose-headings:font-bold prose-headings:tracking-tight
                  prose-h2:mt-10 prose-h2:border-b prose-h2:border-slate-200 prose-h2:pb-3 prose-h2:text-2xl dark:prose-h2:border-slate-800
                  prose-h3:text-xl
                  prose-p:leading-8
                  prose-li:leading-8
                  prose-a:font-bold prose-a:text-[#1277B9] prose-a:no-underline hover:prose-a:underline
                  prose-strong:text-slate-950 dark:prose-strong:text-white
                "
              >
                {children}
              </div>
            </article>
          </Reveal>

          <Reveal>
            <aside className="sticky top-24 space-y-5">
              <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#1277B9]/10 text-[#1277B9] dark:bg-[#1277B9]/20 dark:text-sky-300">
                    <FiFileText className="h-5 w-5" />
                  </span>

                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                      Éditeur du site
                    </p>
                    <h2 className="mt-1 text-lg font-bold text-slate-950 dark:text-white">
                      {identity.name}
                    </h2>
                  </div>
                </div>

                <div className="mt-5 space-y-3 text-sm font-semibold text-slate-600 dark:text-slate-300">
                  {identity.address && (
                    <div className="flex items-start gap-3">
                      <FiMapPin className="mt-1 h-4 w-4 shrink-0 text-[#1277B9]" />
                      <span className="whitespace-pre-line">
                        {identity.address}
                      </span>
                    </div>
                  )}

                  {identity.phone && (
                    <div className="flex items-start gap-3">
                      <FiPhone className="mt-1 h-4 w-4 shrink-0 text-[#1277B9]" />
                      <a
                        href={`tel:${String(identity.phone).replace(/\s+/g, "")}`}
                        className="hover:text-[#1277B9] hover:underline"
                      >
                        {identity.phone}
                      </a>
                    </div>
                  )}

                  {identity.email && (
                    <div className="flex items-start gap-3">
                      <FiMail className="mt-1 h-4 w-4 shrink-0 text-[#1277B9]" />
                      <a
                        href={`mailto:${identity.email}`}
                        className="break-all text-[#1277B9] hover:underline"
                      >
                        {identity.email}
                      </a>
                    </div>
                  )}

                  {identity.siteUrl && (
                    <div className="flex items-start gap-3">
                      <FiExternalLink className="mt-1 h-4 w-4 shrink-0 text-[#1277B9]" />
                      <a
                        href={identity.siteUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="break-all text-[#1277B9] hover:underline"
                      >
                        {identity.siteUrl}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-[2rem] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900/70">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                    <FiShield className="h-5 w-5" />
                  </span>

                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                      Protection des données
                    </p>
                    <p className="mt-1 text-sm font-bold text-slate-950 dark:text-white">
                      Contact DPO / RGPD
                    </p>
                  </div>
                </div>

                <p className="mt-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
                  Pour toute demande relative à vos données personnelles, vous
                  pouvez contacter la mairie.
                </p>

                {identity.dpoEmail && (
                  <a
                    href={`mailto:${identity.dpoEmail}`}
                    className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl bg-[#1277B9] px-4 text-sm font-bold text-white transition hover:bg-[#0f659d]"
                  >
                    <FiMail className="h-4 w-4" />
                    Contacter la mairie
                  </a>
                )}
              </div>
            </aside>
          </Reveal>
        </div>
      </main>
    </>
  );
}